import type { ContactPoint, HumanName, Patient } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import {
  CodeSelectInput,
  DateInput,
  StringInput,
} from '../../components/inputs';
import { useFhirEditState } from '../../core/FhirEditScope';
import type { FhirView } from '../../core/view-contract';

/**
 * Demographics edit variant — the same card as PatientHeaderView with the
 * facts swapped for inputs. All edits go through the edit scope's store
 * (immutable copies of the resource); the app's save bar commits them.
 */

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
] as const;

type Gender = NonNullable<Patient['gender']>;

/** The name being edited: the official name when present, else the first. */
function editableNameIndex(patient: Patient): number {
  const index =
    patient.name?.findIndex((name) => name.use === 'official') ?? -1;
  return index >= 0 ? index : 0;
}

function withName(patient: Patient, update: Partial<HumanName>): Patient {
  const names = [...(patient.name ?? [{}])];
  const index = editableNameIndex(patient);
  names[index] = { ...names[index], ...update };
  return { ...patient, name: names };
}

function withTelecom(
  patient: Patient,
  system: 'phone' | 'email',
  value: string | undefined,
): Patient {
  const telecom = [...(patient.telecom ?? [])];
  const index = telecom.findIndex((point) => point.system === system);
  if (value === undefined) {
    if (index >= 0) telecom.splice(index, 1);
  } else {
    const next: ContactPoint = { system, value };
    if (index >= 0) telecom[index] = { ...telecom[index], value };
    else telecom.push(next);
  }
  return { ...patient, telecom: telecom.length > 0 ? telecom : undefined };
}

function withAddress(
  patient: Patient,
  update: Partial<{
    line: string | undefined;
    city: string | undefined;
    state: string | undefined;
    postalCode: string | undefined;
  }>,
): Patient {
  const address = { ...(patient.address?.[0] ?? {}) };
  if ('line' in update) {
    address.line = update.line ? [update.line] : undefined;
  }
  if ('city' in update) address.city = update.city;
  if ('state' in update) address.state = update.state;
  if ('postalCode' in update) address.postalCode = update.postalCode;
  return {
    ...patient,
    address: [address, ...(patient.address?.slice(1) ?? [])],
  };
}

export const PatientEditView: FhirView<Patient> = ({ data }) => {
  const { current, setCurrent, dirty } = useFhirEditState(data);
  const name = current.name?.[editableNameIndex(current)];
  const phone = current.telecom?.find((p) => p.system === 'phone')?.value;
  const email = current.telecom?.find((p) => p.system === 'email')?.value;
  const address = current.address?.[0];

  return (
    <div
      data-view="patient-edit"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          Demographics
        </Text>
        {dirty && <Tag pill subtle size="xs" color="warning" label="Edited" />}
      </Stack>
      <Grid columns={2} className="mt-3 gap-x-6 gap-y-3 sm:grid-cols-4">
        <StringInput
          label="Given name"
          value={name?.given?.join(' ')}
          onChange={(next) =>
            setCurrent(
              withName(current, { given: next ? next.split(' ') : undefined }),
            )
          }
        />
        <StringInput
          label="Family name"
          value={name?.family}
          onChange={(next) => setCurrent(withName(current, { family: next }))}
        />
        <CodeSelectInput
          label="Gender"
          value={current.gender as Gender | undefined}
          options={GENDER_OPTIONS}
          onChange={(next) => setCurrent({ ...current, gender: next })}
        />
        <DateInput
          label="Date of birth"
          value={current.birthDate}
          onChange={(next) => setCurrent({ ...current, birthDate: next })}
        />
        <StringInput
          label="Phone"
          type="tel"
          value={phone}
          onChange={(next) => setCurrent(withTelecom(current, 'phone', next))}
        />
        <StringInput
          label="Email"
          type="email"
          value={email}
          onChange={(next) => setCurrent(withTelecom(current, 'email', next))}
        />
        <StringInput
          label="Street"
          value={address?.line?.join(' ')}
          onChange={(next) => setCurrent(withAddress(current, { line: next }))}
        />
        <StringInput
          label="City"
          value={address?.city}
          onChange={(next) => setCurrent(withAddress(current, { city: next }))}
        />
        <StringInput
          label="State"
          value={address?.state}
          onChange={(next) => setCurrent(withAddress(current, { state: next }))}
        />
        <StringInput
          label="Postal code"
          value={address?.postalCode}
          onChange={(next) =>
            setCurrent(withAddress(current, { postalCode: next }))
          }
        />
      </Grid>
    </div>
  );
};
